import React, { useState, useEffect, useContext, useCallback } from 'react'
import { useLocation } from 'react-router-dom'
import { IconButton, Divider, Box } from '@mui/material'
import { listProjectViews } from '../api'
import {
    getOffset,
    Pagination,
    SearchFilter,
    MoreVertIcon,
    ProjectsIcon,
    usePagination,
    useSearchParams,
    formatDate,
    defaultBackButton,
} from '../components'
import { AppContext } from '../context'
import { Griddable } from '../grid'
import { translate } from '../i18n'
import {
    Project,
    ProjectView,
    ProjectViewListParams,
    ProjectViews,
} from '../model'
import { RouterParams } from '../router'

export default function ProjectViewsList({ match }: RouterParams) {
    const context = useContext(AppContext)
    const paramTenantId = match.params.tenantId
    const paramProjectId = match.params.projectId
    const tenantId = context.session?.tenant?.id ?? paramTenantId

    const query = new URLSearchParams(useLocation().search)
    const params = useSearchParams<ProjectViewListParams>(query)

    const [status, setStatus] = useState<'loading' | 'loaded' | Error>(
        'loading'
    )
    const [data, setData] = useState<ProjectViews>()

    const clearCallback = useCallback(() => setData(undefined), [])
    const { page, pageSize, onChangedPage, onChangedPageSize } =
        usePagination(clearCallback)

    const retrievePromise = useCallback(() => {
        const offset = getOffset(page, pageSize)
        return listProjectViews(tenantId, paramProjectId, {
            ...params,
            page: pageSize,
            offset,
        })
    }, [tenantId, paramProjectId, page, pageSize, params])

    const reload = useCallback(() => {
        setStatus('loading')
        retrievePromise()
            .then((response) => {
                setData(response)
                setStatus('loaded')
            })
            .catch((error) => {
                setStatus(error)
            })
    }, [retrievePromise])

    useEffect(reload, [reload])

    return (
        <Pagination
            title={translate('project_views.title')}
            subtitle={data?.project?.name}
            icon={<ProjectsIcon />}
            page={page}
            pageSize={pageSize}
            count={data ? data.items.length : 0}
            total={data ? data.total : 0}
            onChangedPage={onChangedPage}
            onChangedPageSize={onChangedPageSize}
            backButton={defaultBackButton}
        >
            <SearchFilter
                label={translate('project_views.filter') as string}
                query={query}
            />
            <Box mx={-2} mt={2}>
                <Divider />
                <Griddable
                    items={data ? data.items : []}
                    loading={status === 'loading'}
                    error={status instanceof Error ? status.message : undefined}
                    empty={translate('project_views.empty') as string}
                    getId={(projectView: ProjectView) => projectView.id}
                    refreshable={{
                        columnId: 'actions',
                        onClick: reload,
                    }}
                    columns={[
                        {
                            title: translate('project_views.os') as string,
                            converter: (projectView: ProjectView) =>
                                projectView.os || '---',
                            id: 'os',
                        },
                        {
                            title: translate('project_views.browser') as string,
                            converter: (projectView: ProjectView) =>
                                projectView.browser || '---',
                            id: 'browser',
                        },
                        {
                            title: translate(
                                'project_views.ip_address'
                            ) as string,
                            converter: (projectView: ProjectView) =>
                                projectView.ip_address || '---',
                            id: 'ip_address',
                        },
                        {
                            title: translate('project_views.country') as string,
                            converter: (projectView: ProjectView) =>
                                projectView.country || '---',
                            id: 'country',
                        },
                        {
                            title: translate('project_views.state') as string,
                            converter: (projectView: ProjectView) =>
                                projectView.state || '---',
                            id: 'state',
                        },
                        {
                            title: translate('project_views.city') as string,
                            converter: (projectView: ProjectView) =>
                                projectView.city || '---',
                            id: 'city',
                        },
                        {
                            title: translate(
                                'project_views.created_at'
                            ) as string,
                            converter: (projectView: ProjectView) =>
                                formatDate({ date: projectView.created_at }),
                            id: 'created_at',
                        },
                        {
                            title: (
                                <IconButton
                                    size="small"
                                    style={{ visibility: 'hidden' }}
                                    disabled
                                >
                                    <MoreVertIcon />
                                </IconButton>
                            ),
                            converter: (project: Project) => (
                                <IconButton size="small" disabled>
                                    <MoreVertIcon />
                                </IconButton>
                            ),
                            id: 'actions',
                            fixed: 'right',
                            width: 60,
                            textAlign: 'center',
                        },
                    ]}
                />
            </Box>
        </Pagination>
    )
}
